/**
 * ProtectedRoute — gate de rutas autenticadas + shell.
 *
 * Comportamiento:
 * - status === 'loading' → renderiza un splash mínimo (no redirige todavía).
 *   Razón: si redirigimos durante hidratación, el usuario que recarga /home
 *   ve un flash a /login antes de aterrizar de vuelta. Mejor splash 100ms.
 * - status === 'anonymous' → redirige a /login con `from` en state para
 *   volver tras login.
 * - status === 'authenticated' → renderiza children + BottomNav (s64).
 *
 * s64: la barra inferior móvil (Inicio · Plan · Carreras) se monta AQUÍ,
 * una sola vez para toda la app autenticada — nunca en login/registro
 * (no pasan por este gate). `bottomNav={false}` la apaga en rutas
 * donde no procede (onboarding).
 */

import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";

import { BottomNav } from "@/components/nav/BottomNav";
import { useAuth } from "@/hooks/useAuth";

interface ProtectedRouteProps {
  children: ReactNode;
  /** Barra inferior móvil. Default true; false en onboarding. */
  bottomNav?: boolean;
}

export function ProtectedRoute({
  children,
  bottomNav = true,
}: ProtectedRouteProps) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    // Splash mínimo, sin spinner agresivo — coherente con marca sobria.
    return (
      <div
        className="flex h-screen items-center justify-center text-bone-muted"
        role="status"
        aria-live="polite"
      >
        Cargando…
      </div>
    );
  }

  if (status === "anonymous") {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return (
    <>
      {children}
      {bottomNav && <BottomNav />}
    </>
  );
}
